/**
 * Milo, Imprion TV's assistant, as a picture: the 3D figure where the device can draw it and
 * a still pose where it cannot.
 *
 * SHARED FILE — byte-identical in tv-display, tv-guest and tv-admin (`src/libs/milo/`);
 * change all three together (CLAUDE.md "Milo 3D"; tv-display's `tools/milo.test.mjs` compares
 * them). It imports nothing of its app, and no `three`: `three` and the scene are behind the
 * dynamic import in `createMilo`, so an app pays for them only when Milo is on the screen.
 *
 * The model and the still poses are served ONCE, by tv-display under `/screen/milo/<version>/`
 * on the shared domain (as the console and the guest app already borrow its fonts), in a
 * versioned folder so a browser may keep them as long as it likes.
 */

/** What Milo is doing. The 3D figure has a pose for each; the stills cover four of them. */
export type MiloState = 'idle' | 'listen' | 'laugh' | 'think' | 'wink' | 'speak' | 'greet'

export const MILO_STATES: readonly MiloState[] = ['idle', 'listen', 'laugh', 'think', 'wink', 'speak', 'greet']

/** The folder with the model, the still poses and three.js's licence. */
export const MILO_ASSETS = '/screen/milo/1.0.0/'

export const MILO_MODEL_URL = `${MILO_ASSETS}milo.glb`

/** The still poses that were rendered (tv-display `tools/milo-poses/render.sh`). */
type MiloPose = 'idle' | 'think' | 'speak' | 'greet' | 'listen'

const POSE_OF: Record<MiloState, MiloPose> = {
    idle:   'idle',
    listen: 'listen',
    laugh:  'speak',
    think:  'think',
    wink:   'idle',
    speak:  'speak',
    greet:  'greet',
}

/**
 * The still picture for a state: a transparent WebP rendered from the model itself, so the
 * fallback and the figure are the same Milo. `large` is the whole figure at 720 × 810 (the room
 * screen); the small one head and shoulders at 480 × 480 (a header, a drawer — 240 px at 2×).
 */
export const miloPose = (state: MiloState, large = false): string =>
    `${MILO_ASSETS}pose-${POSE_OF[state]}${large ? '' : '-sm'}.webp`

/** How the figure is drawn on this device. */
export interface IMiloOptions {
    /** Where the GLB is. */
    modelUrl?:    string
    /** The device pixel ratio is capped at this (1 on a TV, 2 on a phone). */
    maxPixelRatio: number
    antialias:    boolean
    /** Frames per second at most; 0 = the display's own rate. */
    fps:          number
    /**
     * Frames per second while the figure stands still — idle, the eyes settled, no blink on its
     * way — when only the breathing moves; it goes back to `fps` the moment anything else does.
     * Keep it above `minFps`. Absent = `fps`.
     */
    idleFps?:     number
    /**
     * A weak GPU: plain PBR materials instead of the model's clearcoat and no environment
     * map, which roughly halves the work per pixel.
     */
    lowPower:     boolean
    /** Head and shoulders instead of the whole figure (a small avatar). */
    framing:      'full' | 'head'
    /** The eyes follow the pointer (a phone's finger, a mouse). */
    trackPointer: boolean
    /**
     * Give up when the device draws fewer frames than this a second, measured after the
     * first second; `onFail` is then called with 'slow'. 0 = never.
     */
    minFps:       number
    /** WebGL broke after the start (context lost, a shader that does not compile, too slow). */
    onFail?:      (reason: string) => void
}

/** The figure, once it stands on its canvas. */
export interface IMiloScene {
    setState(state: MiloState): void
    /** Eyes towards a point on the page (client pixels): a focused chip on the TV. */
    lookAt(x: number, y: number): void
    /** Eyes straight ahead. */
    lookAhead(): void
    setTracking(enabled: boolean): void
    /** Draws frames from now on. */
    start(): void
    /** Draws nothing until `start` (the screen is not in front). */
    stop(): void
    /** Everything released, the GL context included. */
    dispose(): void
}

/**
 * Whether this engine can draw the figure: WebGL 2 (three.js has needed it since r163), and
 * not the legacy bundle (tv-display's `nomodule` build for engines without ES modules, which
 * is the old Tizen floor — a still pose there). `strict` also refuses a software renderer
 * (`failIfMajorPerformanceCaveat`), which a TV would draw at a frame a second.
 */
export const canRender3d = (strict: boolean): boolean => {
    if (import.meta.env.LEGACY) return false

    try {
        const canvas = document.createElement('canvas')
        const gl = canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: strict })

        if (!gl) return false

        gl.getExtension('WEBGL_lose_context')?.loseContext()

        return true
    } catch {
        return false
    }
}

/**
 * Loads three.js and the model and puts the figure on `canvas`. Throws where anything on the
 * way fails (the chunk, the model, the context) — the caller shows the still pose then.
 */
export const createMilo = async (canvas: HTMLCanvasElement, options: IMiloOptions): Promise<IMiloScene> => {
    const { MiloScene } = await import('./miloScene')
    const scene = new MiloScene(canvas, options)

    await scene.load(options.modelUrl ?? MILO_MODEL_URL)

    return scene
}
